import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Form, Input } from 'antd';
import { Braces } from 'lucide-react';
import BuilderDetectionPanel from '@/components/BuilderDetectionPanel';
import { IndexingPanel } from '@/components/workspace-settings/IndexingPanel';
import { COPY } from '@/lib/copyTokens';
import { useRegisterDirtyForm } from '@/components/settings/DirtyFormGuard';
import { SectionPage } from './SectionPage';
import type { TenantSettings } from '@/types/tenant';
import { useSectionTenantSettings } from './useSectionTenantSettings';

type WebsiteValues = Pick<TenantSettings, 'locations_wp_template_name'>;

export default function WebsiteSection() {
  const [form] = Form.useForm<WebsiteValues>();
  const [isDirty, setIsDirty] = useState(false);
  const { tenantId, tenantSettings, isLoading, isSaving, save } = useSectionTenantSettings();

  useEffect(() => {
    form.setFieldsValue({ locations_wp_template_name: tenantSettings?.locations_wp_template_name });
    setIsDirty(false);
  }, [form, tenantSettings]);

  const submit = useCallback(async () => {
    const values = await form.validateFields();
    const succeeded = await save({ locations_wp_template_name: values.locations_wp_template_name });
    if (succeeded) setIsDirty(false);
    return succeeded;
  }, [form, save]);

  const discard = useCallback(() => {
    form.setFieldsValue({ locations_wp_template_name: tenantSettings?.locations_wp_template_name });
    setIsDirty(false);
  }, [form, tenantSettings]);

  useRegisterDirtyForm(
    useMemo(
      () => ({ isDirty: () => isDirty, save: submit, discard }),
      [discard, isDirty, submit],
    ),
  );

  if (!tenantId) {
    return (
      <SectionPage sectionId="website">
        <p className="text-sm text-ink-2">{COPY.chooseEntity}</p>
      </SectionPage>
    );
  }

  return (
    <SectionPage sectionId="website">
      <div className="max-w-4xl space-y-9">
        <section aria-labelledby="page-builder-heading" className="space-y-4">
          <h3 id="page-builder-heading" className="text-sm font-semibold tracking-tight text-ink">
            {COPY.sections.websiteInfo.pageBuilder}
          </h3>
          <p className="text-sm text-ink-2">{COPY.sections.websiteInfo.pageBuilderHelp}</p>
          <div className="relative overflow-hidden rounded-xl border border-border bg-card p-5">
            <div aria-hidden="true" className="absolute inset-y-0 left-0 w-1 bg-gradient-to-b from-teal to-coral" />
            <div className="flex items-start gap-3">
              <div className="rounded-lg border border-border bg-paper p-2 text-teal">
                <Braces aria-hidden="true" className="h-4 w-4" />
              </div>
              <div className="min-w-0 flex-1">
                <BuilderDetectionPanel website={tenantId} settings={tenantSettings} />
              </div>
            </div>
          </div>
        </section>

        <section aria-labelledby="site-indexing-heading" className="space-y-4">
          <h3 id="site-indexing-heading" className="text-sm font-semibold tracking-tight text-ink">
            {COPY.sections.websiteInfo.siteIndexing}
          </h3>
          <p className="text-sm text-ink-2">{COPY.sections.websiteInfo.siteIndexingHelp}</p>
          <div className="relative overflow-hidden rounded-xl border border-border bg-card p-5">
            <div aria-hidden="true" className="absolute inset-y-0 left-0 w-1 bg-gradient-to-b from-teal to-coral" />
            <div className="flex items-start gap-3">
              <div className="rounded-lg border border-border bg-paper p-2 text-teal">
                <Braces aria-hidden="true" className="h-4 w-4" />
              </div>
              <div className="min-w-0 flex-1">
                <IndexingPanel tenantId={tenantId} />
              </div>
            </div>
          </div>
        </section>

        <section aria-labelledby="location-template-heading" className="space-y-4">
          <h3 id="location-template-heading" className="text-sm font-semibold tracking-tight text-ink">
            {COPY.sections.websiteInfo.locationPageTemplate}
          </h3>
          <Form<WebsiteValues>
            form={form}
            layout="vertical"
            onFinish={submit}
            onValuesChange={() => setIsDirty(true)}
          >
            <Form.Item
              name="locations_wp_template_name"
              label={COPY.sections.websiteInfo.locationPageTemplate}
              extra={COPY.sections.websiteInfo.locationPageTemplateHelp}
            >
              <Input className="w-full" />
            </Form.Item>
            <Button type="primary" htmlType="submit" loading={isLoading || isSaving}>
              {COPY.cards.dirtyGuard.save}
            </Button>
          </Form>
        </section>
      </div>
    </SectionPage>
  );
}
